import { useQuery, useMutation } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { FileWithPath } from 'react-dropzone'
import type { IDirectory, IUserFile } from '~/types/user-file'

import {
  createDirectory,
  createUserFile,
  deleteDirectory,
  deleteUserFile,
  getUserFilesByDirectory,
  updateDirectoryLocation,
  updateFileLocation,
  uploadUserFile,
} from '~/api/user-file'

////////// KEYS //////////

export const keys = {
  all: ['user-files'] as const,
  listByDirectory: (directoryId: string) => [...keys.all, 'list', 'directory', directoryId] as const,
}

////////// QUERIES //////////

export const useGetUserFilesByDirectoryQuery = (
  directoryId: string
): UseQueryResult<{
  breadcrumb: { directory_id: string; name: string; path: string }[]
  content?: IUserFile[]
  current_directory: IDirectory
}> => {
  return useQuery({
    queryFn: () => getUserFilesByDirectory({ directoryId }),
    queryKey: keys.listByDirectory(directoryId),
  })
}

////////// MUTATIONS //////////

export const useCreateUserFileMutation = (): UseMutationResult<
  IUserFile,
  unknown,
  { directory_id: string; file_id: string },
  unknown
> => {
  return useMutation({ mutationFn: createUserFile })
}

export const useDeleteUserFileMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: deleteUserFile })
}

export const useUploadUserFileMutation = (): UseMutationResult<
  void,
  unknown,
  { download_url: string; file: FileWithPath },
  unknown
> => {
  return useMutation({ mutationFn: uploadUserFile })
}

export const useCreateDirectoryMutation = (): UseMutationResult<
  IDirectory,
  unknown,
  { name: string; path: string },
  unknown
> => {
  return useMutation({ mutationFn: createDirectory })
}

export const useDeleteDirectoryMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: deleteDirectory })
}

export const useUpdateFileLocationMutation = (): UseMutationResult<
  void,
  unknown,
  { directoryId: string; fileId: string },
  unknown
> => {
  return useMutation({ mutationFn: updateFileLocation })
}

export const useUpdateDirectoryLocationMutation = (): UseMutationResult<
  void,
  unknown,
  { directoryId: string; name?: string; path: string },
  unknown
> => {
  return useMutation({ mutationFn: updateDirectoryLocation })
}
